export const USE_COMPASSUS_NAVIGATOR_V2 = true;

const compassusNavigatorPath = `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator${
  USE_COMPASSUS_NAVIGATOR_V2 ? "/v2" : ""
}`;

export const APIRoutes = {
  apiAuthPreFlight: "/patient-centric-app-v2/auth/pf",
  apiGetDotPhrasesList: "/patient-centric-app-v2/dot-phrases/of_type",
  // mix panel error store
  apiMixPanelErrorStore: `/patient-centric-app-v2/mixpanel/log-error`,

  // User Signup:
  apiSignup: `/patient-centric-app-v2/admin/v3/users/complete-signup`,

  // User Agreement
  userAgreement: `/user-agreement/userAgreement`,
  getPatientsList: `/patient-centric-app-v2/clinic/patient_list`,
  // Single-patient billing/profile snapshot (payer_information_json, extra_info,
  // condition_json, and the saved `diagnosis` selection) — queried with
  // ?clinic_id=&patient_id=. Used by the Order Note panel to self-source payer
  // rules + the saved diagnosis selection without going through the billing list.
  apiGetPatientListV2: `/patient-centric-app-v2/clinic/patient_list_v2`,

  // Clinics
  apiMyClinic: `/patient-centric-app-v2/clinic/my-clinic-slim`,
  apiMyClinicFull: `/patient-centric-app-v2/clinic/my-clinic`,
  apiUpdateFacilityInformation: `/patient-centric-app-v2/clinic/{clinic_id}/extra-info`,
  apiClinicContactInfo: `/patient-centric-app-v2/clinic/{clinic_id}/contact-info`,
  apiClinicConfig: `/patient-centric-app-v2/clinic/{clinic_id}/config`,
  apiClinicShiftReportConfig: `/patient-centric-app-v2/clinic/{clinic_id}/shift_report_config`,
  apiGetClinicFloor: `/patient-centric-app-v2/clinic/{clinic_id}/user-pref/floors`,
  apiSetClinicFloor: `/patient-centric-app-v2/clinic/{clinic_id}/user-pref/floors`,
  apiPsnSettings: `/patient-centric-app-v2/clinic/{clinic_id}/psn_settings`,
  apiResidentList: `/patient-centric-app-v2/clinic/patient_list`,
  apiClinicDailyReportClinic: `/patient-centric-app-v2/v2/clinic_report`,

  // Alert
  apiGetAlertListNew: `/patient-centric-app-v2/clinic/{clinic_id}/v2/clinical-alerts`,
  apiGetAlertDetail: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{resident_id}/v3/clinical-alerts/{alert_id}`,
  apiAddAlertComment: `/patient-centric-app-v2/clinic/{clinic_id}/acknowledge/alert/{alert_id}`,
  apiGetPSNAck: `/patient-centric-app-v2/clinic/{clinic_id}/PSN/alert/{alert_id}`,
  apiAddPSNAck: `/patient-centric-app-v2/clinic/{clinic_id}/psn_v2/alert/{alert_id}/ack`,
  apiGetCombinedAlertList: `/patient-centric-app-v2/clinic/{clinic_id}/patients/escalated-flagged/v2`,
  apiGetFlagList: `/patient-centric-app-v2/clinic/{clinic_id}/v2/patients/flagged`,
  apiAddAlertAck: `/patient-centric-app-v2/clinic/{clinic_id}/ack/alerts/v2`,
  apiPatientAlert: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/escalated-flagged/v2`,
  apiGetFlagAlertClinical: `/patient-centric-app-v2/clinic/{clinic_id}/clinical-listing`,
  apiHideFlag: `/patient-centric-app-v2/clinic/{clinic_id}/psn_v2/alert/{flagged_id}/hide`,
  apiGetInboxFlagsByReviewStatus: `/patient-centric-app-v2/clinic/{clinic_id}/inbox/flags`,
  // Patient-centric flags API (coworker's new endpoint)
  // Patient flags — paginated v1 endpoint used by the Patient/Flags page
  // and the PatientProfile clinical views. Accepts `page` + `page_size`.
  apiGetPatientFlags: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/flags`,
  // Patient flags — date-windowed v2 endpoint used by MonthlyReportModal's
  // SBAR section. Returns the trailing 30 days ending on `from_date`
  // (`YYYY-MM-DD`). Different shape semantics from the v1 path above
  // (no pagination), so a separate route + controller method keeps the
  // existing v1 consumers untouched. Mirrors legacy `apiGetPatientFlags`
  // in `Circadia-Web-Portal config.js`.
  apiGetPatientFlagsByMonth: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/v2/flags`,

  // Billing Module (legacy "billingNew" port — listing + signing + billing)
  // Used by src/pages/BillingModule/ via billingModuleController.
  //
  // NOTE: "Bulk Chart Review" has NO dedicated endpoint — it is implemented as
  // N parallel calls to apiUpdateBillingStatus (one per selected patient).
  //
  // Pending Tasks V3 — unified monthly patient list. Replaces the legacy
  // `/billing/v11/clinic/monthly` listing endpoint. The response envelope is
  // different (paginated `{ paging, summary, data: V3PatientRow[] }` instead
  // of `{ data: { list_of_data: BillingRow[] } }`) — the service adapts rows
  // back to the legacy `BillingRow` shape via `src/util/billingV2ToLegacy.ts`.
  apiGetPendingTasksV3: `/patient-centric-app-v2/pending-tasks-v3`,
  apiGetBillingPatientMonthly: `/patient-centric-app-v2/billing/v7/patient/monthly/{patient_id}`,
  apiGetSignOffAssessment: `/patient-centric-app-v2/clinic/{clinic_id}/patient/billing-status/sign-off/{patient_id}/{billing_date}`,
  apiUpdateBillingStatus: `/patient-centric-app-v2/clinic/{clinic_id}/patient/billing-status/v5/{patient_id}/{billing_date}`,
  apiUpdateSignOffAssessment: `/patient-centric-app-v2/clinic/{clinic_id}/patient/billing-status/sign-off/{patient_id}/{billing_date}`,
  // Order Note (ClinicPatientCarePlan) creation — POST, v5 endpoint.
  apiCreateCarePlan: `/patient-centric-app-v2/clinic/{clinic_id}/patient/care-plan/v5/{patient_id}`,
  // Order Note (ClinicPatientCarePlan) edit — PUT, v2 endpoint.
  apiUpdateCarePlan: `/patient-centric-app-v2/clinic/{clinic_id}/patient/care-plan/v2/{patient_id}/{care_plan_id}`,
  // Save / replace a patient's selected diagnoses — POST, replaces the full list.
  apiSaveDiagnosis: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/diagnosis`,
  // Patient-month billing charges (MonthlyReportModal R2).
  //
  // Different from `apiGetBillingCharges` below (`/billing/charges`) — that one
  // is the *admin* listing with pagination/filter query params. The endpoint
  // here returns a narrow per-patient list scoped by `month` + `year` query
  // params, response shape is `BillingChargesByPatientMonthResponse` in
  // `src/hexagon/core/ports/BillingModule.d.ts`.
  apiGetBillingChargesByPatientMonth: `/patient-centric-app-v2/billing/charges/clinic/{clinic_id}/patient/{patient_id}`,

  // Billing / Payers
  apiGetBillingPayers: `/patient-centric-app-v2/billing/payers`,
  apiCreateBillingPayer: `/patient-centric-app-v2/billing/payers`,
  apiUpdateBillingPayer: `/patient-centric-app-v2/billing/payers/{id}`,
  apiDeleteBillingPayer: `/patient-centric-app-v2/billing/payers/{id}`,

  // Billing / Providers
  apiGetBillingProviders: `/patient-centric-app-v2/billing/providers`,
  apiCreateBillingProvider: `/patient-centric-app-v2/billing/providers`,
  apiUpdateBillingProvider: `/patient-centric-app-v2/billing/providers/{id}`,
  apiDeleteBillingProvider: `/patient-centric-app-v2/billing/providers/{id}`,

  // Billing / Users
  apiGetBillingUsers: `/patient-centric-app-v2/billing/users`,

  // Billing / Entity
  apiGetBillingEntity: `/patient-centric-app-v2/billing/entities`,
  apiCreateBillingEntity: `/patient-centric-app-v2/billing/entity`,
  apiUpdateBillingEntity: `/patient-centric-app-v2/billing/entity/{id}`,
  apiDeleteBillingEntity: `/patient-centric-app-v2/billing/entity/{id}`,

  // Billing / Clinics
  apiGetBillingClinics: `/patient-centric-app-v2/billing/clinics`,
  apiUpdateBillingClinic: `/patient-centric-app-v2/billing/clinics/{clinic_id}`,

  // Billing / Roster
  apiGetBillingRosters: `/patient-centric-app-v2/billing/rosters`,
  apiCreateBillingRoster: `/patient-centric-app-v2/billing/rosters`,
  apiUpdateBillingRoster: `/patient-centric-app-v2/billing/rosters/{id}`,
  apiDeleteBillingRoster: `/patient-centric-app-v2/billing/rosters/{id}`,

  // Billing / Patients
  apiGetBillingPatients: `/patient-centric-app-v2/billing/patients`,
  apiCreateBillingPatient: `/patient-centric-app-v2/billing/patients`,
  apiUpdateBillingPatient: `/patient-centric-app-v2/billing/patients/{id}`,
  apiDeleteBillingPatient: `/patient-centric-app-v2/billing/patients/{id}`,

  // Billing / Charges
  apiGetBillingCharges: `/patient-centric-app-v2/billing/charges`,
  apiCreateBillingCharges: `/patient-centric-app-v2/billing/charges`,
  apiUpdateBillingCharges: `/patient-centric-app-v2/billing/charges/{id}`,
  apiDeleteBillingCharges: `/patient-centric-app-v2/billing/charges/{id}`,
  apiGetBillingChargesStatusSummary: `/patient-centric-app-v2/billing/charges/summary/status`,

  //Dashboard
  apiGetDashboardList: `/patient-centric-app-v2/clinic/{clinic_id}/cip/v2/psn-alerts`,
  apiGetPsnAlertGraphDataList: `/patient-centric-app-v2/clinic/{clinic_id}/patients/daily_rr_graph`,
  apiGetPatientListDashboard: `/patient-centric-app-v2/clinic/{clinic_id}/v2/recent/admissions`,
  apiGetReAdmitRadatMostCritical: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/v2/readmit_radar/most-critical`,

  //s3
  apiGetFileFromS3: "/patient-centric-app-v2/aws/document",

  //support
  apiReportAProblem: `/patient-centric-app-v2/clinic/{clinic_id}/report-problem/cip/customer-support`,
  apiSubmitFeedback: `/patient-centric-app-v2/feedback/cip`,
  apiSubmitRating: `/patient-centric-app-v2/delighted/rating`,

  //push Notifications
  apiStoreToken: `/patient-centric-app-v2/v3/token`,
  apiGetNotificationSettings: `/patient-centric-app-v2/user/v4`,
  apiPutNotificationSettings: `/patient-centric-app-v2/user/preference/notification/v4`,
  apiNasaTlxPreference: `/patient-centric-app-v2/user/preference/nasa-tlx`,

  //SMS
  apiSendOTP: `/patient-centric-app-v2/twilio/send-otp`,
  apiVerifyOTP: `/patient-centric-app-v2/verify-otp`,
  apiGetPortalUserInfo: `/patient-centric-app-v2/user_info`,
  apiSavePhoneNumber: `/patient-centric-app-v2/user_info/user_phone_number`,

  // Neo texting (reply-YES double opt-in): bind creates a pending binding and
  // sends the confirmation SMS; status reports pending/verified/none (the
  // inbound Twilio webhook flips it to verified when the user replies YES).
  apiNeoTextingStatus: `/patient-centric-app-v2/neo/texting/status`,
  apiNeoTextingBind: `/patient-centric-app-v2/neo/texting/bind`,
  apiNeoTextingRevoke: `/patient-centric-app-v2/neo/texting/revoke`,

  // Telehealth
  telehealthSessions: `/patient-centric-app-v2/telehealth/sessions`,
  telehealthAssignableProviders: `/patient-centric-app-v2/telehealth/clinics/{clinic_id}/assignable-providers`,
  telehealthAssignableTechnicians: `/patient-centric-app-v2/telehealth/clinics/{clinic_id}/assignable-technicians`,
  telehealthDashboardSessions: `/patient-centric-app-v2/telehealth/sessions/dashboard`,
  telehealthActiveSessionsByVisitNotes: `/patient-centric-app-v2/telehealth/sessions/active-by-visit-notes`,
  telehealthSession: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}`,
  telehealthSessionSmsOutcomes: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}/sms-outcomes`,
  telehealthSessionByVisitNote: `/patient-centric-app-v2/telehealth/sessions/by-visit-note/{visit_note_id}`,
  telehealthPatientSessions: `/patient-centric-app-v2/telehealth/patients/{patient_id}/sessions`,
  telehealthPatientLatestContact: `/patient-centric-app-v2/telehealth/patients/{patient_id}/latest-contact`,
  telehealthClinicianToken: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}/clinician-token`,
  telehealthSessionLinks: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}/links`,
  telehealthShareLink: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}/share-link`,
  telehealthRevokeLink: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}/revoke-link`,
  telehealthEndSession: `/patient-centric-app-v2/telehealth/sessions/{telehealth_session_id}/end`,
  telehealthPublicJoin: `/patient-centric-app-v2/public/telehealth/join/{token}`,
  telehealthPublicToken: `/patient-centric-app-v2/public/telehealth/join/{token}/token`,
  telehealthPublicLeave: `/patient-centric-app-v2/public/telehealth/join/{token}/leave`,

  //report
  apiGetClinicalReport: `/patient-centric-app-v2/clinic/{clinic_id}/daily_reports`,
  apiGetDailyReport: `/patient-centric-app-v2/clinic/{clinic_id}/daily_reports/{daily_report_id}`,
  apiAddReadDailyReport: `/patient-centric-app-v2/clinic/{clinic_id}/daily_reports/{daily_report_id}/read`,
  // report - AI risk report
  apiGetRiskReport: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/v4/gpt/{date}`,
  apiGetReadmitRadar: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/readmit_radar/v4`,
  apiGetRiskReportClinicPreference: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/gpt/clinic_preference`,
  apiGetRiskReportList: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/list/v2`,
  apiAckRiskReport: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/ack/idt_report/{idt_report_id}`,

  // report - AI risk report public routes
  apiGetRiskReportPublicRoute: `/patient-centric-app-v2/idt_reports/link/summaries`,
  apiGetReadmitRadarPublicRoute: `/patient-centric-app-v2/idt_reports/link/readmit_radar`,
  apiGetRiskReportClinicPreferencePublicRoute: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/gpt/clinic_preference`,
  apiGetRiskReportListPublicRoute: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/list-`,
  apiAckRiskReportPublicRoute: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/ack/idt_report/{idt_report_id}-`,

  //patient
  apiCreatePatient: `/patient-centric-app-v2/patient`,
  apiGetPatientList: `/patient-centric-app-v2/clinic/{clinic_id}/v2/patients`,
  apiGetPatientEventList: `/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/v5/events`,
  apiGetPatientSummary: `/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/v4/patient-chart-summary`,
  // VNS-only free-form "Cohort" label linked to a patient admission (GET + PUT).
  apiVnsPatientAdmissionCohort: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/admission/{admission_id}/vns-cohort`,
  apiGetPatientCircadiaVital: `/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/last-circadia-vitals`,
  apiGetPatientPCCVital: `/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/last-pcc-vitals`,
  apiGetPatientRespRate: `/patient-centric-app-v2/clinic/{clinic_id}/patients/rr-metrics-psn`,
  apiGetPatientVitalGraph: `/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/pcc-vitals-history`,
  apiGetPatientRRGraph: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{resident_id}/rr_graph`,
  apiGetPatientNotes: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{resident_id}/cip/combined-circadia-notes`,
  // v3 PCC notes listing. Supports an optional `note_type` query param to
  // filter by a specific PointClickCare note type (from the available types
  // dropdown); omit the param to return all note types.
  apiGetPatientPCCNotes: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{resident_id}/v3/pcc-notes`,
  // Monthly-window PCC notes (v4, date-filtered). Returns the last 30 days of
  // notes ending at `from_date` — used by MonthlyReportModal's PCC Notes
  // section. Mirrors `Circadia-Web-Portal apiGetAllPccNotesOfPatient`.
  apiGetPatientPCCNotesByMonth: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/v4/pcc-notes`,
  apiAddPatientNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{resident_id}/v2/notes`,
  // search
  apiGetSearch: `/patient-centric-app-v2/clinic/{clinic_id}/v3/patients`,

  apiGetAllPatientList: `/patient-centric-app-v2/clinic/{clinic_id}/resident/listing`,

  // Get all patients list (room assigned and not assigned) - V2 API
  apiGetAllPatientListV2: `/patient-centric-app-v2/clinic/{clinic_id}/v2/resident/listing`,

  // device
  apiOfflineDevice: "/patient-centric-app-v2/clinic/{clinic_id}/device/offline",
  // apiAllDevice: "/patient-centric-app/clinic/device/status",
  apiAllDevice: "/patient-centric-app-v2/clinic/{clinic_id}/devices",
  apiRegisterPatientDevice:
    "/patient-centric-app-v2/patient-app/clinic/{clinic_id}/register-device",
  apiPatientAssignedDevice:
    "/patient-centric-app-v2/patient-app/clinic/{clinic_id}/patient/{patient_id}/device",
  apiRemovePatientDevice:
    "/patient-centric-app-v2/admin/devices/{clinic_id}/{device_id}/remove-device",
  apiPatientDeviceConnectionStatus:
    "/patient-centric-app-v2/patient-app/device/{device_id}/connection-status",
  apiPatientAppPatientAccount:
    "/patient-centric-app-v2/patient-app/clinic/{clinic_id}/patient/{patient_id}/account",
  apiCreatePatientAppUserV2: "/patient-centric-app-v2/patient-app/users/v2",

  //insights
  apiGetTOCAllowedMonths:
    "/patient-centric-app-v2/clinic/{clinic_id}/TOC/allowed_months",
  apiGetTOPAllowedMonths:
    "/patient-centric-app-v2/clinic/{clinic_id}/TOP/allowed_months",
  apiGetDischargeAllowedMonths:
    "/patient-centric-app-v2/clinic/{clinic_id}/discharge_insights/allowed_months",
  apiGetTOC: "/patient-centric-app-v2/clinic/{clinic_id}/v2/TOC",
  apiGetTOP: "/patient-centric-app-v2/clinic/{clinic_id}/TOP",
  // Transfers-out prevented (TOP) rollup — MTD + YTD counts for a facility.
  // Powers the "Transfers Prevented" impact card on the Inbox.
  apiGetTOPPreventedSummary:
    "/patient-centric-app-v2/clinic/{clinic_id}/TOP/prevented_summary",
  // Consolidated KPI rollup for the Dashboard Overview screen — clinical impact,
  // flags, and operational metrics for a facility in a single call. v2 drops the
  // (slow) facility Neo cost, which now has its own endpoint (apiGetFacilityNeoCost)
  // so the rest of the dashboard renders without waiting on it.
  apiGetDashboardOverview:
    "/patient-centric-app-v2/clinic/{clinic_id}/dashboard/overview/v2",
  // Facility Neo (AI agent) spend for a single clinic over a window. Split out of
  // the overview rollup because it's the slowest metric to compute; powers the
  // "Facility Neo spend" card (loaded behind a skeleton).
  apiGetFacilityNeoCost:
    "/patient-centric-app-v2/clinic/{clinic_id}/dashboard/neo-cost",
  // Derived Neo (AI agent) spend for the CURRENT user, cross-clinic, over a
  // window. Powers the personal "Your Neo spend" card on the Overview screen.
  apiGetMyNeoCost: "/patient-centric-app-v2/neo/cost/me",
  apiGetMyNeoCredits: "/patient-centric-app-v2/neo/credits/me",
  // Superadmin-only Neo cost lookups (audit): any user by email, any clinic,
  // and the Neo-clinic picker list. Backend 403s non-`sa` callers.
  apiGetUserNeoCost: "/patient-centric-app-v2/neo/cost/user",
  apiGetTopUsersNeoCost: "/patient-centric-app-v2/neo/cost/top_users",
  // Superadmin audit of MIRRORED (localhost/dev/staging) Neo usage uploaded to
  // prod — used to verify the localhost metering upload is flowing.
  apiGetLocalhostTopUsersNeoCost:
    "/patient-centric-app-v2/neo/cost/localhost_top_users",
  apiGetTopWorkflowsNeoCost: "/patient-centric-app-v2/neo/cost/top_workflows",
  apiToggleWorkflowTriggerNeoCost:
    "/patient-centric-app-v2/neo/cost/top_workflows/{workflow_id}/toggle_trigger",
  apiGetTopClinicsNeoCost: "/patient-centric-app-v2/neo/cost/top_clinics",
  apiGetClinicNeoCost: "/patient-centric-app-v2/neo/cost/clinic/{clinic_id}",
  // Superadmin clinic-workflow administration (Product Utilization > Clinic
  // Workflows tab). Backed by the Neo workflows router; enforces per-clinic
  // access server-side.
  apiClinicWorkflowSummary: "/patient-centric-app-v2/neo/workflows/summary",
  // Superadmin DBA editor for the workflow catalog
  // (default_available_clinic_workflows + workflow_category_mapping).
  apiClinicWorkflowCatalog: "/patient-centric-app-v2/neo/workflows/catalog",
  // Superadmin DBA editor for the paid-clinic flag (Product Utilization >
  // Paid Clinics tab). GET lists, POST upserts a clinic's paid flag.
  apiNeoPaidClinics: "/patient-centric-app-v2/neo/credits/paid-clinics",
  // Superadmin free-trial management (Product Utilization > Free Trials tab).
  // GET {base}/{clinic_id} for status; POST {base}/{clinic_id}/start|end|renew.
  apiNeoClinicTrials: "/patient-centric-app-v2/neo/credits/trials",
  apiListClinicWorkflows:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/enabled",
  apiListClinicWorkflowRuns:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/runs",
  apiGetClinicWorkflowStatus:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/{workflow_id_str}/status",
  apiGetClinicWorkflowArtifactUrl:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/runs/{run_id}/artifacts/{file_id}/download-url",
  apiGetClinicWorkflowRecipients:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/{workflow_id_str}/recipients",
  apiGetClinicWorkflowRollingCost:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/{workflow_id_str}/cost/rolling-average",
  apiSetClinicWorkflowUser:
    "/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/{workflow_id_str}/users/{user_id}",
  apiListClinicUsers:
    "/patient-centric-app-v2/patient-app/clinic/{clinic_id}/users",
  apiGetDischarge:
    "/patient-centric-app-v2/clinic/{clinic_id}/v2/discharge_insights",
  apiGetTOCRootCause:
    "/patient-centric-app-v2/clinic/{clinic_id}/transfer_out/{TO_EVENT_ID}/RCA",
  apiGetTOCEventNote:
    "/patient-centric-app-v2/clinic/{clinic_id}/transfer_out/{TO_EVENT_ID}/note",
  apiPutTOCEventNote:
    "/patient-centric-app-v2/clinic/{clinic_id}/transfer_out/{TO_EVENT_ID}/note",

  // Outcomes
  apiGetOutcomesFacilities:
    "/patient-centric-app-v2/clinic/{clinic_id}/outcomes/facilities",
  apiGetOutcomesAvailablePeriods:
    "/patient-centric-app-v2/clinic/{clinic_id}/outcomes/available_periods",
  apiGetOutcomesTimeSeries:
    "/patient-centric-app-v2/clinic/{clinic_id}/outcomes/time_series",
  apiGetOutcomesFilterOptions:
    "/patient-centric-app-v2/clinic/{clinic_id}/outcomes/filter_options",

  //watchlist
  apiGetWatchlist: `/patient-centric-app-v2/clinic/{clinic_id}/my/watchlist/v2/patients/details`,
  apiPutWatchlist: `/patient-centric-app-v2/clinic/{clinic_id}/my/watchlist/change`,
  apiPatchWatchlist: `/patient-centric-app-v2/clinic/{clinic_id}/my/watchlist/change`,

  //consent
  apiGetConsentList: `/patient-centric-app-v2/clinic/{clinic_id}/consent-email-ledger/consent_listing`,
  apiGetConsentDischargeList: `/patient-centric-app-v2/clinic/{clinic_id}/consent-email-ledger/v2/discharge/all_resident`,
  apiPostConsentChange: `/patient-centric-app-v2/clinic/{clinic_id}/consent-email-ledger/v4/change`,

  //CDL Endpoints
  apiPatientGraphData: `/circadia-vitals/patient_processed/{patient_id}/`,
  apiPatientGraphDataV2: `/v2/circadia-vitals/patient_processed/`,
  // Intercom
  apiGetUserHmac: "/patient-centric-app-v2/user/intercom/token",

  // Circadia IQ
  apiGenerateCircadiaIQ:
    "/patient-centric-app-v2/circadia_iq/v4/{clinic_id}/patient/{patient_id}",
  apiGetCircadiaIQ:
    "/patient-centric-app-v2/circadia_iq/v4/{clinic_id}/patient/{patient_id}/db",
  // CircadiaIQ for Recertification — used by the billing monthly report.
  // Returns the cron-generated narrative ("recertification_note") supporting
  // continued SNF + remote monitoring necessity for the patient.
  apiGetCircadiaIQForRecertification:
    "/patient-centric-app-v2/cronjob/recert_iq/{clinic_id}/patient/{patient_id}",
  // Order IQ — AI-generated medical necessity summary for the RPM order note.
  apiGetOrderIQ:
    "/patient-centric-app-v2/cronjob/order_iq/{clinic_id}/patient/{patient_id}",

  // bed exits
  apiAddBedExits: `/patient-centric-app-v2/user/bed-exit/settings/v2`,
  apiAddBedExitsV3: `/patient-centric-app-v2/clinic/{clinic_id}/user/bed-exit/settings/v3`,
  apiUpdateBedExitsTime: `/patient-centric-app-v2/user/bed-exit/settings/time`,
  apiUpdateBedExits: `/patient-centric-app-v2/user/bed-exit/settings/toggle/v3`,
  apiGetBedExitSettingsByUserPref: `/patient-centric-app-v2/clinic/{clinic_id}/user/bed-exits/v2`,
  apiGetPatientNotificationSettings: `/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/bed-exits/v2`,
  apiGetDiagnoses:
    "/patient-centric-app-v2/clinic/{clinic_id}/resident/{resident_id}/patient-diagnosis",
  apiUpdatePatientNotificationSettings: `/patient-centric-app-v2/user/bed-exit/settings/v2`,
  apiUpdatePatientNotificationSettingsV2: `/patient-centric-app-v2/user/bed-exit/settings/v3`,
  apiUpdateCombineBedExits: `/patient-centric-app-v2/user/bed-exit/settings/combine`,

  // Bed Exit Event List
  apiGetBedExitEventList:
    "/patient-centric-app-v2/clinic/{clinic_id}/v2/bed-exit-events",

  apiGetBedExitFacilityReport:
    "/patient-centric-app-v2/clinic/{clinic_id}/bed-exit-comparison",

  // Users
  apiUserBetaFeature: `/patient-centric-app-v2/user/beta-feature`,
  // Grants a beta feature to a target (query params: feature, target_type,
  // email, description). Used to self-enroll the session user into
  // user-scoped beta features (e.g. patient-page-v2).
  apiAdminBetaFeature: `/patient-centric-app-v2/admin/beta-feature/v1`,
  apiAutoBedExit: `/patient-centric-app-v2/user/auto-bed-exit`,

  // Tracking
  apiTracking: `/`,

  // Summary
  apiGetSummary: `/patient-centric-app-v2/clinic/{clinic_id}/v4/alerts/summary`,

  // IPV
  apiGetIPVNotesList: `/patient-centric-app-v2/clinic/{clinic_id}/v2/ipv_notes?status={status}`,
  apiAddIPVNotesAck: `/patient-centric-app-v2/clinic/{clinic_id}/acknowledge/ipv_note/{note_id}`,

  // Device Issues
  apiPostDeviceIssue: `/patient-centric-app-v2/clinic/{clinic_id}/device-issues`,

  // Home Health apis
  apiGetSinglePatientInfo: "/home_health/patient/{patient_id}",
  apiGetPatientGraphData: "/home_health/patient/{patient_id}/readings",
  apiGetSelectedFacilityPatientList: "/home_health/facility/{clinic_id}/",
  // User Info
  apiUpdateUserInfo: `/patient-centric-app-v2/user/v2/name`,

  // Sales
  apiAddSalesAck: `/patient-centric-app-v2/clinic/{clinic_id}/demo-request`,

  // RT Blogger
  apiPostParseRTAR: `/pcc_blogger/clinic/{clinic_id}/parse_pdf`,
  apiPostRTARBilling: `/pcc_blogger/clinic/{clinic_id}/patient/{patient_id}/generate_billing/{month}/{year}`,
  apiGetBillingSettings: `/pcc_blogger/rt-billing/clinic/{clinic_id}/settings`,
  apiUpdateBillingSettings: `/pcc_blogger/rt-billing/clinic/{clinic_id}/settings`,
  apiGetServices: `/pcc_blogger/rt-billing/services`,
  apiUpdateServices: `/pcc_blogger/rt-billing/services`,
  apiGetAudit: `/pcc_blogger/rt-billing/clinic/{clinic_id}/audit_settings`,
  apiUpdateAudit: `/pcc_blogger/rt-billing/clinic/{clinic_id}/audit_settings`,
  apiGetCPTTypes: `/pcc_blogger/rt-billing/cpt-types`,
  apiPutBillingServiceStatus: `/pcc_blogger/rt-billing/clinic/{clinic_id}/patient/{patient_id}/billing-service-logs`,
  apiGetOverviewPatients: `/pcc_blogger/rt-billing/clinic/{clinic_id}/billing-service-logs/{month}/{year}`,
  apiGetBillingCycle: `/pcc_blogger/rt-billing/clinic/{clinic_id}/billing-cycles`,
  apiGetBillingCycleForPatient: `/pcc_blogger/rt-billing/clinic/{clinic_id}/patient/{patient_id}/billing-service-logs/{month}/{year}`,
  apiDeleteBillingCycle: `/pcc_blogger/rt-billing/clinic/{clinic_id}/billing-cycles/{month}/{year}`,
  apiPutBillingCycleStatusForPatient: `/pcc_blogger/rt-billing/clinic/{clinic_id}/patient/{patient_id}/billing-cycles/{month}/{year}`,
  apiDeleteBillingCycleForPatient: `/pcc_blogger/rt-billing/clinic/{clinic_id}/patient/{patient_id}/billing-cycles/{month}/{year}`,
  apiPostBillingFiles: `/pcc_blogger/rt-billing/clinic/{clinic_id}/billing-files`,
  apiPostFixError: `/pcc_blogger/rt-billing/clinic/{clinic_id}/patient/{patient_id}/fix-errors`,

  // VitalSync
  apiExtractVitals: `/patient-centric-app-v2/clinic/{clinic_id}/extract-vitals`,
  apiSubmitVitalsToPCC: `/patient-centric-app-v2/clinic/{clinic_id}/pcc/submit`,
  apiGetPCCSubmissionStatistics: `/patient-centric-app-v2/clinic/{clinic_id}/pcc/submission/statistics`,
  apiGetPCCQueueList: `/patient-centric-app-v2/clinic/{clinic_id}/pcc/queue/list`,
  apiGetPCCQueueStatus: `/patient-centric-app-v2/clinic/{clinic_id}/pcc/queue/status`,
  apiGetPCCSubmissionHistory: `/patient-centric-app-v2/clinic/{clinic_id}/pcc/submission/history`,
  apiGetNeedReviewVitals: `/patient-centric-app-v2/admin/v4/clinics/{clinic_id}/need-review-vitals`,
  apiUpdateNeedReviewVitals: `/patient-centric-app-v2/admin/v4/clinics/{clinic_id}/need-review-vitals`,

  // Enrolment
  apiGetEnrolledPatients: `/pcc_blogger/clinic/{clinic_id}/patients`,
  apiGetPreviouslyEnrolledPatients: `/rtx/patients/status_base_list/{clinic_id}`,
  apiGetPatientEligibility: `/patient-centric-app-v2/patient-eligibility/{patient_id}`,
  apiRefreshEligibility: `/patient-centric-app-v2/patient/{patient_id}/eligibility/{eligibility_id}`,
  apiPutEnrollPatient: `/pcc_blogger/enrolment/new/{admission_id}`,
  apiPutExtendEnrollPatient: `/pcc_blogger/enrolment/update/{rtx_enrolment_id}`,

  // Billing Reports
  apiGetBillingReports: `/pcc_blogger/rt-billing/clinic/{clinic_id}/reports`,
  apiUpdateBillingReportCollectedAmount: `/pcc_blogger/rt-billing/clinic/{clinic_id}/update_collections/{month}/{year}`,

  // PSN v2 Review
  apiPutPSNAcknowledge: `/patient-centric-app-v2/clinic/{clinic_id}/psn_v2/shift_report/{notification_uuid}/ack`,

  apiPostAppPerformanceSurvey: `/patient-centric-app-v2/delighted/nps`,

  // Column Preferences
  apiGetColumnPreferences: `/patient-centric-app-v2/user-preferences/column-preferences`,
  apiPutColumnPreferences: `/patient-centric-app-v2/user-preferences/column-preferences`,
  apiPostColumnPreferencePreset: `/patient-centric-app-v2/user-preferences/column-preferences/presets`,
  apiPutColumnPreferencePreset: `/patient-centric-app-v2/user-preferences/column-preferences/presets/{preset_id}`,
  apiDeleteColumnPreferencePreset: `/patient-centric-app-v2/user-preferences/column-preferences/presets/{preset_id}`,
  apiApplyColumnPreferencePreset: `/patient-centric-app-v2/user-preferences/column-preferences/presets/{preset_id}/apply`,

  // Feature Tours
  apiGetFeatureTours: `/patient-centric-app-v2/user-preferences/feature-tours`,
  apiGetFeatureTourStatus: `/patient-centric-app-v2/user-preferences/feature-tours/status`,
  apiCompleteFeatureTourStep: `/patient-centric-app-v2/user-preferences/feature-tours/steps/complete`,
  apiGetFacilities: "/patient-centric-app-v2/clinics",
  apiGetFacilitiesV2: "/patient-centric-app-v2/v2/clinics",
  apiGetAllFacilities: "/patient-centric-app-v2/admin/clinics",

  apiAssignFacility: "/patient-centric-app-v2/users/{userId}/clinics",
  apiProvisionCompassusClinics:
    "/patient-centric-app-v2/clinics/provision-compassus",

  // HealthScribe APIs - V1 (Legacy - kept for backward compatibility)
  healthScribeTranscriptionList: `/patient-centric-app-v2/healthscribe/transcription/list`,
  healthScribeGetSession: `/patient-centric-app-v2/healthscribe/recording/clinic/{clinic_id}/session/{session_id}`,
  healthScribeListSessions: `/patient-centric-app-v2/healthscribe/recording/clinic/{clinic_id}/sessions`,
  healthScribeS3UploadInit: `/patient-centric-app-v2/healthscribe/s3/upload/init`,
  healthScribeS3UploadPart: `/patient-centric-app-v2/healthscribe/s3/upload/part`,
  healthScribeS3UploadComplete: `/patient-centric-app-v2/healthscribe/s3/upload/complete`,
  healthScribeS3UploadCompleteAudio: `/patient-centric-app-v2/healthscribe/s3/upload/audio`,
  healthScribeS3UploadAbort: `/patient-centric-app-v2/healthscribe/s3/clinic/{clinic_id}/upload/{session_id}/{upload_id}`,
  healthScribeS3UploadStatus: `/patient-centric-app-v2/healthscribe/s3/clinic/{clinic_id}/upload/status/{session_id}/{upload_id}`,

  // HealthScribe APIs - V2 (Multi-Segment with Pause/Resume)
  healthScribeV2StartRecording: `/patient-centric-app-v2/healthscribe/v2/recording/start`,
  healthScribeV2StopRecording: `/patient-centric-app-v2/healthscribe/v2/recording/stop`,
  healthScribeV2TranscriptionBatchSave: `/patient-centric-app-v2/healthscribe/v2/transcription/batch-save`,
  healthScribeV2NarrativeGenerate: `/patient-centric-app-v2/healthscribe/v2/narrative/generate`,
  healthScribeV2NarrativeResult: `/patient-centric-app-v2/healthscribe/v2/narrative/result`,
  healthScribeV2SessionComplete: `/patient-centric-app-v2/healthscribe/v2/session/complete`,
  healthScribeV2GetSession: `/patient-centric-app-v2/healthscribe/v2/session/{session_id}`,
  healthScribeV2ListSegments: `/patient-centric-app-v2/healthscribe/v2/session/{session_id}/segments`,
  healthScribeV2ListSessions: `/patient-centric-app-v2/healthscribe/v2/clinic/{clinic_id}/sessions`,

  // HealthScribe APIs - Heidi STT Integration (replaces WebSocket streaming)
  healthScribeHeidiTranscribe: `/patient-centric-app-v2/healthscribe/heidi/transcribe/{session_id}`,
  healthScribeHeidiTranscript: `/patient-centric-app-v2/healthscribe/heidi/transcript/{session_id}`,
  healthScribeHeidiRecover: `/patient-centric-app-v2/healthscribe/heidi/recover`,
  healthScribeHeidiAppendTranscript: `/patient-centric-app-v2/healthscribe/heidi/append-transcript/{session_id}`,
  // Combined transcribe + generate endpoint (async background processing)
  healthScribeHeidiTranscribeAndGenerate: `/patient-centric-app-v2/healthscribe/heidi/transcribe-and-generate/{session_id}`,
  healthScribeHeidiTranscribeAndGenerateResult: `/patient-centric-app-v2/healthscribe/heidi/transcribe-and-generate/result/{session_id}`,
  healthScribeHeidiGenerateArtifacts: `/patient-centric-app-v2/healthscribe/heidi/generate-artifacts/{session_id}`,
  healthScribeHeidiRegenerateBilling: `/patient-centric-app-v2/healthscribe/heidi/regenerate-billing/{session_id}`,
  healthScribeHeidiCompassusUserMapping: `/patient-centric-app-v2/healthscribe/heidi-user-mapping/compassus`,
  healthScribeHeidiCompassusUserMappingsAll: `/patient-centric-app-v2/healthscribe/heidi-user-mapping/compassus/all`,
  healthScribeHeidiCompassusLinkAccount: `/patient-centric-app-v2/healthscribe/heidi-user-mapping/compassus/link-account`,

  // Initial Patient Visit (IPV)
  apiGetIPVNotesByClinicID: `/patient-centric-app-v2/clinic/{clinic_id}/ipv_notes?status={status}`,
  apiGetIPVNotesByClinicIDv3: `/patient-centric-app-v2/clinic/{clinic_id}/v3/ipv_notes`,
  apiGetIPVNotesPatientsList: `/patient-centric-app-v2/clinic/{clinic_id}/v3/patients/ipv_notes`,
  apiPostIPVNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ipv_notes`,
  apiGetPatientExtraInfo: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/extra-info`,
  apiPostPatientExtraInfo: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/extra-info`,
  apiPatchPatientSavedAddresses: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/saved-addresses`,
  apiGetIPVNotesByPatientID: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ipv_notes`,
  // Monthly Report backfill: all completed notes created in ?month=YYYY-MM
  // (any doc_type) + the first completed np_visit of each admission
  // overlapping that month. Full `notes` JSON bodies included.
  apiGetIPVNotesForMonthlyReport: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/monthly-report/ipv_notes`,
  apiGetNpSupervisingPhysician: `/patient-centric-app-v2/user/nps/{np_user_id}/supervising-physician`,
  apiDeleteIPVNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ipv_notes/{ipv_note_id}`,
  apiGetPatientsForNavigator: `/patient-centric-app-v2/clinic/{clinic_id}/navigator/patients`,
  apiGetNavigatorPatientById: `/patient-centric-app-v2/clinic/{clinic_id}/navigator/patients/{patient_id}`,
  apiPatchNavigatorIpvIneligible: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/admissions/{admission_id}/navigator-ipv-ineligible`,
  apiGetEstimatedIpvTodoCount: `/patient-centric-app-v2/navigator/estimated-ipv-todo-count`,
  apiGetClinicsWithShifts: `/patient-centric-app-v2/navigator/clinics-with-shifts`,
  apiCompassusProductivity: `/patient-centric-app-v2/compassus/productivity`,
  apiCompassusProductivitySummary: `/patient-centric-app-v2/compassus/productivity/summary`,
  apiCompassusProductivityProviders: `/patient-centric-app-v2/compassus/productivity/providers`,
  apiCompassusProductivitySnowflakeUtilization: `/patient-centric-app-v2/compassus/productivity/snowflake-utilization`,
  apiCompassusProductivityEngagementUtilization: `/patient-centric-app-v2/compassus/productivity/engagement-utilization`,
  apiCompassusProductivityMe: `/patient-centric-app-v2/compassus/productivity/me`,
  apiCompassusProductivityPtoRecords: `/patient-centric-app-v2/compassus/productivity/pto-records`,
  apiCompassusProductivityPtoRecord: `/patient-centric-app-v2/compassus/productivity/pto-records/{pto_record_id}`,
  apiCompassusProductivityMonthlyRecords: `/patient-centric-app-v2/compassus/productivity/monthly-records`,
  apiCompassusProductivityMonthlyRecord: `/patient-centric-app-v2/compassus/productivity/monthly-records/{monthly_record_id}`,
  apiCompassusProductivityMonthlyAssumptions: `/patient-centric-app-v2/compassus/productivity/monthly-assumptions`,
  apiCompassusProductivityMonthlyAssumption: `/patient-centric-app-v2/compassus/productivity/monthly-assumptions/{assumption_id}`,
  apiCompassusProductivityProviderProfile: `/patient-centric-app-v2/compassus/productivity/provider-profiles/{provider_user_id}`,
  apiCompassusProductivityNasaTlxFeedback: `/patient-centric-app-v2/compassus/productivity/nasa-tlx-feedback`,
  apiCompassusNpMySchedule: `/patient-centric-app-v2/compassus/np/my-schedule`,
  apiCompassusNpMyPatients: `/patient-centric-app-v2/compassus/np/my-patients`,
  apiCompassusNpPatients: `/patient-centric-app-v2/compassus/np/patients`,
  apiGetPatientVisitNotes: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes`,
  apiGetAtRiskReviewLockStatus: `/patient-centric-app-v2/clinic/{clinic_id}/at-risk-review-lock-status`,
  apiPostAtRiskReviewOverrideRequest: `/patient-centric-app-v2/clinic/{clinic_id}/at-risk-review-request`,
  apiPatchAtRiskReviewOverrideRequest: `/patient-centric-app-v2/clinic/{clinic_id}/at-risk-review-request`,

  // Navigator Custom Todos
  apiPostCustomTodo: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/custom-todo`,
  apiPatchCustomTodo: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/custom-todo/{todo_id}`,
  apiDeleteCustomTodo: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/custom-todo/{todo_id}`,
  apiGetCustomTodoVisits: `/patient-centric-app-v2/clinic/{clinic_id}/navigator/custom-todo-visits`,
  apiGetClinicCustomTodoRecurrences: `/patient-centric-app-v2/clinic/{clinic_id}/navigator/custom-todo-recurrences`,
  apiCustomTodoRecurrence: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/custom-todo-recurrence`,
  apiCustomTodoRecurrenceById: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/custom-todo-recurrence/{schedule_id}`,

  // Device Orders
  apiDeviceOrders: `/patient-centric-app-v2/clinic/{clinic_id}/device-orders`,
  apiDeviceOrderById: `/patient-centric-app-v2/clinic/{clinic_id}/device-orders/{order_id}`,

  // Patient Notes History. Compassus callers append clinic_id so imported
  // notes are restricted to the active clinic's inferred service line.
  apiGetPatientNotesHistory: `/patient-centric-app-v2/patient/{patient_id}/notes`,

  apiPutIPVNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ipv_notes/{ipv_note_id}`,
  apiDeleteIPVDraftAndSession: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/recording-draft`,

  // HPI Template APIs
  apiGetHPITemplates: `/patient-centric-app-v2/HPI/template/list`,
  apiCreateHPITemplate: `/patient-centric-app-v2/HPI/template/create`,
  apiGetSingleHPITemplate: `/patient-centric-app-v2/HPI/template/{template_id}`,
  apiUpdateHPITemplate: `/patient-centric-app-v2/HPI/template/update`,
  apiDeleteHPITemplate: `/patient-centric-app-v2/HPI/template/delete/{template_id}`,

  // Unified Visit Notes API (CREATE and UPDATE only - draft loading still uses legacy IPV API)
  apiCreateVisitNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes`,
  apiUpdateVisitNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}`,
  apiRefreshPastContextPrechart: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/past-context-prechart`,
  apiCarePlanPrechart: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/care-plan-prechart`,
  apiGetAllVisitNotes: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes`,
  // Web list view variant — scopes patients via clinic_patient_admission so a
  // transferred patient's historical notes still appear under the old clinic.
  // Same query params and response shape as apiGetAllVisitNotes. Mobile clients
  // should keep using apiGetAllVisitNotes (lighter scoping, optimized for polling).
  apiGetAllVisitNotesFull: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/full`,
  apiGetScheduledVisitAssignments: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/scheduled-assignments`,
  apiGetVisitNoteProviders: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/providers`,
  apiSearchVisitNotes: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/search`,
  apiGetArchivedVisitNotes: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/archived`,
  apiRestoreArchivedVisitNote: `/patient-centric-app-v2/clinic/{clinic_id}/ipv_notes/{ipv_note_id}/restore`,
  apiGetSingleVisitNote: `/patient-centric-app-v2/visit-notes/{note_id}`,
  apiGetClinicianAcuityCatalog: `/patient-centric-app-v2/visit-notes/clinician-acuity/catalog`,
  apiPutClinicianAssessment: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/clinician-assessment`,
  apiAfterVisitSummary: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/after-visit-summary`,
  apiAfterVisitSummaryPdf: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/after-visit-summary/pdf`,
  apiAfterVisitSummarySend: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/after-visit-summary/send`,
  apiHchbCoordinationNoteTypes: `/patient-centric-app-v2/compassus/clinic/{clinic_id}/patient/{patient_id}/hchb/coordination-note-types`,
  apiRetryCoordinationNote: `/patient-centric-app-v2/compassus/clinic/{clinic_id}/patient/{patient_id}/coordination-notes/{coordination_note_id}/retry`,
  // POST creates + pushes a coordination note to HCHB; GET lists prior pushes.
  apiHchbCoordinationNotes: `/patient-centric-app-v2/compassus/clinic/{clinic_id}/patient/{patient_id}/coordination-notes`,
  apiAssessmentFormTemplates: `/patient-centric-app-v2/clinic/{clinic_id}/assessment-form-templates`,
  apiAssessmentFormTemplate: `/patient-centric-app-v2/clinic/{clinic_id}/assessment-form-templates/{template_id}`,
  apiArchiveAssessmentFormTemplate: `/patient-centric-app-v2/clinic/{clinic_id}/assessment-form-templates/{template_id}/archive`,
  apiPublishAssessmentFormTemplate: `/patient-centric-app-v2/clinic/{clinic_id}/assessment-form-templates/{template_id}/publish`,
  apiVisitNoteAssessmentForms: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/clinician-assessment/forms`,
  apiVisitNoteAssessmentForm: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/clinician-assessment/forms/{template_id}`,
  apiDeleteVisitNoteAssessmentForm: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}/clinician-assessment/forms/{template_id}`,
  apiGetNoteLlmOutput: `/patient-centric-app-v2/healthscribe/heidi/note/{note_id}/llm-output`,
  apiDeleteNoteManualEditsHistory: `/patient-centric-app-v2/healthscribe/heidi/note/{note_id}/manual-edits-history`,

  // Summarize Narrative (for sharing as PDF)
  apiSummarizeNarrative: `/patient-centric-app-v2/healthscribe/heidi/summarize-narrative`,

  // Send Visit Note via eFax
  apiSendVisitNoteEfax: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/efax`,
  apiDownloadPartnershipSummaryDocx: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/partnership-summary/docx`,
  apiGetConsentTemplateMetadata: `/patient-centric-app-v2/consent/template/{consent_type}/metadata`,
  apiGetConsentTemplatePageImage: `/patient-centric-app-v2/consent/template/{consent_type}/page/{page}/image`,
  apiGetHhRpmConsentDraftMetadata: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/patient/{patient_id}/consent/hh-rpm/draft/metadata`,
  apiGetHhRpmPatientConsentDraftMetadata: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/patient/{patient_id}/consent/hh-rpm-patient/draft/metadata`,
  apiGetPatientConsents: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/patient/{patient_id}/consent`,
  apiSubmitHhRpmConsent: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/patient/{patient_id}/consent/hh-rpm`,
  apiSubmitHhRpmPatientConsent: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/patient/{patient_id}/consent/hh-rpm-patient`,

  apiStorePreChartNote: `/patient-centric-app-v2/clinic/{clinic_id}/pre-chart-note`,

  // Twilio Visit Reminder
  apiSendVisitReminder: `/patient-centric-app-v2/twilio/send-visit-reminder`,

  // Discharge PDF APIs
  apiUploadDischargePdf:
    "/patient-centric-app-v2/clinic/{clinic_id}/medication-reconciliation/discharge-pdf/upload",
  apiGetDischargePdfUploads:
    "/patient-centric-app-v2/clinic/{clinic_id}/medication-reconciliation/discharge-pdf/uploads",
  apiGetMedRecAdmissions:
    "/patient-centric-app-v2/clinic/{clinic_id}/v2/medication-reconciliation/admissions",
  apiUpdateFindingStatus:
    "/patient-centric-app-v2/clinic/{clinic_id}/medication-reconciliation/discharge-pdf/upload/{upload_id}/findings/{finding_index}/status",

  // Side Peek Profile APIs
  apiGetSidePeekProfile: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile`,
  apiGenerateSidePeekProfile: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile`,
  apiUpdateSidePeekProfile: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile`,

  // Safety Note APIs
  apiGetSafetyNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/safety-note`,
  apiUpdateSafetyNote: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/safety-note`,

  // Code Status APIs
  apiGetCodeStatus: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/code-status`,
  apiUpdateCodeStatus: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/code-status`,

  // EHR Records APIs
  apiGetEhrMedications: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/medications`,
  apiGetEhrNutrition: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/nutrition`,
  apiGetEhrAssessments: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/assessments`,
  apiGetEhrAllergies: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/allergies`,
  apiGetEhrLabs: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/labs`,
  apiGetEhrLab: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/labs/{lab_id}`,
  apiDownloadEHRLabs: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/labs/{lab_id}/download`,
  // Report-id addressed lab endpoints. Used for reports that live only in PCC
  // (not yet mirrored to ehr_labs), which have no local numeric lab id.
  apiGetEhrLabByReport: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/labs/by-report/{report_id}`,
  apiDownloadEhrLabByReport: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/ehr/labs/by-report/{report_id}/download`,
  apiDownloadMarBackup: `/patient-centric-app-v2/clinic/{clinic_id}/ehr/mar-backup/download`,
  // v2 Assessments APIs pulling directly from PCC
  apiGetEhrAssessmentsV2: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/assessments`,
  apiGetEhrAssessmentsByAssessmentID: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/assessments/{assessment_id}`,
  // PCC incident reports (mirror tables) and single-incident detail
  apiGetPccIncidents: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/incidents`,
  apiGetPccIncidentDetail: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/incidents/{incident_id}`,
  // Care plan APIs pulling directly from PCC
  apiGetCarePlan: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/care-plans`,
  apiGetCarePlanFocusesByFocusIds: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/focuses`,
  // Request an asynchronous full reconcile of a patient's data against PCC
  apiRequestPccReconcile: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/pcc/reconcile`,
  // Available PCC note types for a clinic (used to populate the Notes filter dropdown)
  apiGetPccAvailableNoteTypes: `/patient-centric-app-v2/idt_reports/clinic/{clinic_id}/gpt/available_notes_type`,
  // SSO Token Refresh
  apiSsoRefreshToken: `/patient-centric-app-v2/auth/sso/refresh`,

  // Patient Contacts APIs
  apiGetPatientContacts: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/contacts`,
  apiUpdatePatientContacts: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/contacts`,
  apiPatientCaregivers: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/caregivers`,
  apiPatientCaregiver: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/caregivers/{caregiver_id}`,
  apiPatientPrimaryCareProvider: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/primary-care-provider`,
  apiPatientSpecialists: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/specialists`,
  apiPatientSpecialist: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/specialists/{specialist_id}`,
  apiPatientInsuranceCard: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/insurance-card`,
  apiPatientInsuranceCardImages: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/insurance-card/images`,
  apiPatientInsuranceCardImage: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/insurance-card/images/{side}`,

  // Patient Primary Language API
  apiPatientPrimaryLanguage: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/primary-language`,
  apiPatientTimezone: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/timezone`,

  // Next Visit Plan APIs (kept for reference, superseded by Patient Summary)
  apiGetNextVisitPlan: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/next-visit-plan`,
  apiUpdateNextVisitPlan: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/next-visit-plan`,

  // Patient Summary APIs (sidepeek)
  apiGetSidePeekPatientSummary: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/patient-summary`,
  apiGetSidePeekPatientSummaryStatus: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/patient-summary/status`,
  apiGenerateSidePeekPatientSummary: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/patient-summary`,

  // Geo
  apiValidateAddress: `/patient-centric-app-v2/geo/validate-address`,
  apiAddressSuggestions: `/patient-centric-app-v2/geo/address-suggestions`,
  apiComputeDistances: `/patient-centric-app-v2/geo/compute-distances`,
  apiGetHomeAddress: `/patient-centric-app-v2/geo/home-address`,
  apiSetHomeAddress: `/patient-centric-app-v2/geo/home-address`,

  // NP Scheduling
  apiNpSchedulingShifts: `/patient-centric-app-v2/np-scheduling/shifts`,
  // Per-diem availability lock-in. Backed by `committed_days.py` in
  // Patient-Centric-App-V2; supports `?viewall=true` for the admin tab's
  // monthly projection.
  apiNpSchedulingCommittedDays: `/patient-centric-app-v2/np-scheduling/committed-days`,

  // Temp Download (for Android WebView PDF downloads)
  apiTempDownloadUpload: `/patient-centric-app-v2/clinic/{clinic_id}/visit-notes/temp-download/upload`,

  // Compassus Navigator
  apiCompassusNavigator: compassusNavigatorPath,
  apiCompassusNavigatorExport: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/export`,
  apiCompassusNavigatorCounts: `${compassusNavigatorPath}/counts`,
  apiCompassusNavigatorCompletedVisits: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/completed-visits`,
  apiCompassusNavigatorFilterPayers: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/payers`,
  apiCompassusNavigatorFilterMedications: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/medications`,
  apiCompassusNavigatorFilterAllergies: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/allergies`,
  apiCompassusNavigatorFilterConditions: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/conditions`,
  apiCompassusNavigatorFilterLastVisitProviders: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/last-visit-providers`,
  apiCompassusNavigatorFilterTodoTags: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/todo-tags`,
  apiCompassusNavigatorFilterCustomTags: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/custom-tags`,
  apiCompassusNavigatorFilterClinicalRiskScores: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/clinical-risk-scores`,
  apiCompassusNavigatorFilterNextVisitProviders: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/next-visit-providers`,
  apiCompassusNavigatorPatientHhJourneyStatus: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hh-journey-status`,
  apiCompassusNavigatorPatientHhCadence: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hh-cadence`,
  apiCompassusNavigatorPatientDataReview: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/data-review`,
  apiCompassusNavigatorPatientInsurance: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/insurance`,
  apiCompassusNavigatorPatientInsuranceResponse: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/insurance/response`,
  apiCompassusNavigatorHhEligibilityWorklist: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/hh-eligibility-worklist`,
  apiCompassusNavigatorHhNpWorklist: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/hh-np-worklist`,
  apiCompassusNavigatorHhTechnicianWorklist: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/hh-technician-worklist`,
  apiCompassusNavigatorHhGlobalAdminWorklist: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/hh-global-admin-worklist`,
  apiCompassusNavigatorHhGlobalAdminWorklistCounts: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/hh-global-admin-worklist/counts`,
  apiCompassusNavigatorHhNpUnlinkDraft: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/hh-visit-assignments/{assignment_id}/unlink-draft`,
  apiCompassusNavigatorHhVisitSchedule: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hh-visit-assignments/{assignment_id}/schedule`,
  apiCompassusNavigatorHhVisitCancel: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hh-visit-assignments/{assignment_id}`,
  apiCompassusNavigatorPatientHhFollowUp: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hh-follow-up`,
  apiCompassusNavigatorPatientHhAtRiskFollowUp: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hh-at-risk-follow-up`,
  apiCompassusNavigatorPatientTodo: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/todo`,
  apiCompassusNavigatorPatientTodoBulk: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/todo/bulk`,
  apiCompassusPatientCustomTags: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/custom-tags`,
  apiCompassusPatientHospitalizationRecords: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/hospitalization-records`,
  apiCompassusPatientHospitalizationRecord: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/hospitalization-records/{record_id}`,
  apiCompassusPatientCommunicationNumbers: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/numbers`,
  apiCompassusPatientCommunicationPrimaryNumber: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/numbers/primary`,
  apiCompassusPatientNpAppointmentSms: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/np-appointment-sms`,
  apiCompassusPatientCommunicationSms: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/sms`,
  apiCompassusPatientCommunicationSmsThreads: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/sms/threads`,
  apiCompassusPatientCommunicationCalls: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/calls`,
  apiCompassusPatientCommunicationCallToken: `/patient-centric-app-v2/clinic/{clinic_id}/patient/{patient_id}/communications/calls/token`,
  apiHhRpmCommunicationNotificationCount: `/patient-centric-app-v2/clinic/{clinic_id}/hh-rpm/communications/notifications/count`,
  apiHhRpmCommunicationRegistry: `/patient-centric-app-v2/clinic/{clinic_id}/hh-rpm/communications/registry`,
  apiHhRpmCommunicationThreads: `/patient-centric-app-v2/clinic/{clinic_id}/hh-rpm/communications/threads`,
  apiHhRpmCommunicationNotifications: `/patient-centric-app-v2/clinic/{clinic_id}/hh-rpm/communications/notifications`,
  apiHhRpmCommunicationNotificationReview: `/patient-centric-app-v2/clinic/{clinic_id}/hh-rpm/communications/notifications/{episode_id}/review`,
  apiHhRpmSchedulerAlertRecipients: `/patient-centric-app-v2/clinic/{clinic_id}/hh-rpm/communications/alert-recipients`,
  apiCompassusPatientAssociations: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patient-associations/{patient_id}`,
  // Per-patient trend endpoints. Each returns the full chronological history
  // (newest-first, one entry per source visit, no roll-up) for a single
  // field extracted by the Compassus assessment pipeline. Latest values are
  // also hydrated inline on every /navigator list row via
  // `latest_acuity_*` / `latest_prognosis_*` / `latest_form_*`, so most call
  // sites use those directly and only reach for these endpoints when drawing
  // a trend or detail view on the patient profile.
  apiCompassusNavigatorPatientAcuityLevels: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/acuity-levels`,
  apiCompassusNavigatorPatientPrognoses: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/prognoses`,
  apiCompassusNavigatorPatientForms: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/forms`,
  // Single-patient hospice eligibility / risk score lookup. Returns the
  // same `hospice_rule_total_score`, `hospice_risk_level`, `hospice_hot_list`
  // and `hospice_rule_score_details` that appear inline on each navigator
  // list row, plus the full deterministic rubric (`rubric_json`) and the
  // LLM-derived risk fields (`llm_risk_score`, `llm_evidence_json`) used
  // by the dedicated Hospice Risk tab on the patient profile.
  //
  // Backend uses a LEFT JOIN, so a patient who belongs to the clinic but
  // has no precomputed score yet still returns a 200 with null score
  // fields; a 404 means the patient is not in that clinic.
  apiCompassusNavigatorPatientHospiceEligibilityScore: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/patient/{patient_id}/hospice-eligibility-score`,
  // Append-only score history (oldest → newest) for plotting the Hospice
  // Risk trend graph on the patient profile. A new snapshot is recorded
  // by the backend any time a scoring input changes, so two adjacent
  // points can share the same score with different `scored_at` stamps.
  // Optional query params: `include_rubric=true` to inflate each record
  // with the full rubric snapshot (drilldown), `limit=N` to cap to the
  // most-recent N records (still ordered oldest → newest).
  //
  // Unlike the single-score endpoint (which is clinic-scoped under
  // `/clinic/{clinic_id}/compassus/navigator/…`), the history endpoint
  // is patient-scoped only — clinic access is enforced server-side
  // against the clinic that owns `patient_id`.
  apiCompassusNavigatorPatientHospiceEligibilityScoreHistory: `/patient-centric-app-v2/compassus/patient/{patient_id}/hospice-eligibility-score/history`,
  // Forced LLM refresh for a single patient's hospice eligibility score.
  // Re-runs the deterministic rubric step (keeping the base score +
  // history current) and then forces an LLM refresh, returning the
  // refreshed scores so the FE can render immediately without a follow-up
  // GET. Patient-scoped only — like the history endpoint, clinic access
  // is enforced server-side against the clinic that owns `patient_id`,
  // so no `clinic_id` is needed in the path.
  apiCompassusNavigatorPatientHospiceEligibilityScoreLlmRefresh: `/patient-centric-app-v2/compassus/patient/{patient_id}/hospice-eligibility-score/llm-refresh`,
  apiCompassusNavigatorPatientHospiceEligibilityScoreLlmRefreshStatus: `/patient-centric-app-v2/compassus/patient/{patient_id}/hospice-eligibility-score/llm-refresh/status`,
  llmChatNano: `/patient-centric-app-v2/llm/chat/nano`,
  // Structural schema for every Compassus assessment form. Pure projection
  // of the pipeline templates — body is identical for every clinic, and
  // only changes on code deploy. Safe to cache indefinitely on the client
  // (see useSWRImmutable on the FormTrendCard). Scoped under clinic_id
  // only to keep auth consistent with the other navigator endpoints.
  apiCompassusNavigatorFormsSchema: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/forms/schema`,
  apiCompassusNavigatorFilterAcuityLevels: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/navigator/filters/acuity-levels`,

  // Coordination Notes
  apiCoordNotes: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/coord-notes`,
  apiCoordNote: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/coord-notes/{note_id}`,
  // Compassus Care Plan
  apiCompassusCarePlan: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/care-plan`,
  apiCompassusCarePlanVersions: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/care-plan/versions`,
  apiCompassusCarePlanVersion: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/care-plan/versions/{version_id}`,
  apiCompassusCarePlanVersionDiff: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/care-plan/versions/{version_id}/diff`,
  apiCompassusCarePlanVersionRestore: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/care-plan/versions/{version_id}/restore`,
  apiCompassusCarePlanExport: `/patient-centric-app-v2/clinic/{clinic_id}/compassus/patients/{patient_id}/care-plan/export`,

  // Neo agent - conversations
  apiNeoProjects: `/patient-centric-app-v2/neo/projects`,
  apiNeoProject: `/patient-centric-app-v2/neo/projects/{project_id}`,
  // Neo agent - templates (cloneable known-good starting points)
  apiNeoTemplates: `/patient-centric-app-v2/neo/templates`,
  apiNeoTemplateClone: `/patient-centric-app-v2/neo/templates/{template_id}/clone`,
  apiNeoPromptSuggestions: `/patient-centric-app-v2/neo/prompt-suggestions`,
  apiNeoClinicCareSetting: `/patient-centric-app-v2/neo/clinics/{clinic_id}/care-setting`,
  apiNeoProjectActivity: `/patient-centric-app-v2/neo/projects/{project_id}/activity`,
  apiNeoProjectAgents: `/patient-centric-app-v2/neo/projects/{project_id}/agents`,
  apiNeoProjectAgentFiles: `/patient-centric-app-v2/neo/projects/{project_id}/agents/{agent_id}/files`,
  apiNeoProjectAgentFile: `/patient-centric-app-v2/neo/projects/{project_id}/agents/{agent_id}/files/{file_id}`,
  apiNeoProjectAgentInstructions: `/patient-centric-app-v2/neo/projects/{project_id}/agents/{agent_id}/instructions`,
  apiNeoProjectAgentInstruction: `/patient-centric-app-v2/neo/projects/{project_id}/agents/{agent_id}/instructions/{instruction_id}`,
  apiNeoProjectFiles: `/patient-centric-app-v2/neo/projects/{project_id}/files`,
  apiNeoProjectFile: `/patient-centric-app-v2/neo/projects/{project_id}/files/{file_id}`,
  apiNeoProjectDocuments: `/patient-centric-app-v2/neo/projects/{project_id}/documents`,
  apiNeoProjectDocumentContent: `/patient-centric-app-v2/neo/projects/{project_id}/documents/{file_id}/content`,
  apiNeoProjectDocumentDownloadUrl: `/patient-centric-app-v2/neo/projects/{project_id}/documents/{file_id}/download-url`,
  apiNeoProjectDashboards: `/patient-centric-app-v2/neo/projects/{project_id}/dashboards`,
  apiNeoProjectDashboard: `/patient-centric-app-v2/neo/projects/{project_id}/dashboards/{dashboard_id}`,
  apiNeoProjectDashboardCsvs: `/patient-centric-app-v2/neo/projects/{project_id}/dashboards/{dashboard_id}/csvs`,
  apiNeoProjectDashboardCsv: `/patient-centric-app-v2/neo/projects/{project_id}/dashboards/{dashboard_id}/csvs/{file_id}`,
  apiNeoProjectDashboardCsvContent: `/patient-centric-app-v2/neo/projects/{project_id}/dashboards/{dashboard_id}/csvs/{file_id}/content`,
  apiNeoProjectDashboardCsvXlsx: `/patient-centric-app-v2/neo/projects/{project_id}/dashboards/{dashboard_id}/csvs/{file_id}/content.xlsx`,
  apiNeoProjectInstructions: `/patient-centric-app-v2/neo/projects/{project_id}/instructions`,
  apiNeoProjectShare: `/patient-centric-app-v2/neo/projects/{project_id}/share`,
  apiNeoAiCareSnfSettings: `/patient-centric-app-v2/neo/projects/ai-care-snf/settings`,
  apiNeoProjectOwners: `/patient-centric-app-v2/neo/projects/{project_id}/owners`,
  apiNeoProjectTasks: `/patient-centric-app-v2/neo/projects/{project_id}/tasks`,
  apiNeoProjectTask: `/patient-centric-app-v2/neo/projects/{project_id}/tasks/{task_id}`,
  apiNeoProjectTaskRun: `/patient-centric-app-v2/neo/projects/{project_id}/tasks/{task_id}/run`,
  apiNeoProjectTickets: `/patient-centric-app-v2/neo/projects/{project_id}/tickets`,
  apiNeoProjectTicket: `/patient-centric-app-v2/neo/projects/{project_id}/tickets/{ticket_id}`,
  apiNeoProjectTicketBlocks: `/patient-centric-app-v2/neo/projects/{project_id}/tickets/{ticket_id}/blocks`,
  apiNeoProjectTicketCancel: `/patient-centric-app-v2/neo/projects/{project_id}/tickets/{ticket_id}/cancel`,
  apiNeoConversations: `/patient-centric-app-v2/neo/conversations`,
  apiNeoConversationActivity: `/patient-centric-app-v2/neo/conversations/activity`,
  apiNeoConversationFromFlag: `/patient-centric-app-v2/neo/conversations/from-flag`,
  apiNeoConversation: `/patient-centric-app-v2/neo/conversations/{conversation_id}`,
  apiNeoConversationViewed: `/patient-centric-app-v2/neo/conversations/{conversation_id}/viewed`,
  apiNeoConversationShare: `/patient-centric-app-v2/neo/conversations/{conversation_id}/share`,
  apiNeoConversationScope: `/patient-centric-app-v2/neo/conversations/{conversation_id}/scope`,
  apiNeoConversationScopeHistory: `/patient-centric-app-v2/neo/conversations/{conversation_id}/scope-history`,
  apiNeoConversationFork: `/patient-centric-app-v2/neo/conversations/{conversation_id}/fork`,
  apiNeoConversationBump: `/patient-centric-app-v2/neo/conversations/{conversation_id}/bump-last-message-at`,
  apiNeoConversationTurn: `/patient-centric-app-v2/neo/conversations/{conversation_id}/turn`,
  apiNeoConversationTurnStream: `/patient-centric-app-v2/neo/conversations/{conversation_id}/turn/stream`,

  // Neo agent - messages
  apiNeoMessages: `/patient-centric-app-v2/neo/conversations/{conversation_id}/messages`,
  apiNeoMessageUser: `/patient-centric-app-v2/neo/conversations/{conversation_id}/messages/user`,
  apiNeoMessageAssistantPending: `/patient-centric-app-v2/neo/conversations/{conversation_id}/messages/assistant-pending`,
  apiNeoMessageToolResult: `/patient-centric-app-v2/neo/conversations/{conversation_id}/messages/tool-result`,
  apiNeoMessage: `/patient-centric-app-v2/neo/messages/{message_id}`,
  apiNeoMessageInflight: `/patient-centric-app-v2/neo/messages/inflight`,
  apiNeoMessageMarkStreaming: `/patient-centric-app-v2/neo/messages/{message_id}/mark-streaming`,
  apiNeoMessageOverwriteContent: `/patient-centric-app-v2/neo/messages/{message_id}/overwrite-content`,
  apiNeoMessageComplete: `/patient-centric-app-v2/neo/messages/{message_id}/complete`,
  apiNeoMessageFail: `/patient-centric-app-v2/neo/messages/{message_id}/fail`,
  apiNeoMessageCancel: `/patient-centric-app-v2/neo/messages/{message_id}/cancel`,

  // Neo agent - tool calls
  apiNeoAssistantToolCalls: `/patient-centric-app-v2/neo/messages/{assistant_message_id}/tool-calls`,
  apiNeoAssistantToolCallsValidate: `/patient-centric-app-v2/neo/messages/{assistant_message_id}/tool-calls/validate-depends-on`,
  apiNeoConversationToolCalls: `/patient-centric-app-v2/neo/conversations/{conversation_id}/tool-calls`,
  apiVoiceCallTranscript: `/patient-centric-app-v2/neo/voice-calls/{conversation_id}/transcript`,
  apiVoiceCallAudio: `/patient-centric-app-v2/neo/voice-calls/{conversation_id}/audio`,
  apiNeoToolCallRunning: `/patient-centric-app-v2/neo/tool-calls/{tool_call_row_id}/mark-running`,
  apiNeoToolCallComplete: `/patient-centric-app-v2/neo/tool-calls/{tool_call_row_id}/complete`,
  apiNeoToolCallFail: `/patient-centric-app-v2/neo/tool-calls/{tool_call_row_id}/fail`,
  apiNeoToolCallCancel: `/patient-centric-app-v2/neo/tool-calls/{tool_call_row_id}/cancel`,
  apiNeoToolCallSkip: `/patient-centric-app-v2/neo/tool-calls/{tool_call_row_id}/skip`,
  apiNeoToolCallResultLink: `/patient-centric-app-v2/neo/tool-calls/{tool_call_row_id}/result-message`,

  // Neo agent - action executions (managed-autonomy write approval queue)
  apiNeoActionExecutions: `/patient-centric-app-v2/neo/action-executions`,
  apiNeoActionExecutionApprove: `/patient-centric-app-v2/neo/action-executions/{execution_id}/approve`,
  apiNeoActionExecutionReject: `/patient-centric-app-v2/neo/action-executions/{execution_id}/reject`,
  apiNeoActionExecutionUndo: `/patient-centric-app-v2/neo/action-executions/{execution_id}/undo`,

  // Neo agent - documents (server-side rendering). Two sibling
  // endpoints accept the same body shape and stream different
  // binaries back: ``render-pdf`` for a real vector PDF (WeasyPrint)
  // and ``render-xlsx`` for a .xlsx workbook (openpyxl). Keep both
  // entries paired so anyone adding a third format puts it next to
  // its siblings instead of scattering them across the file.
  apiNeoDocumentRenderPdf: `/patient-centric-app-v2/neo/documents/render-pdf`,
  apiNeoDocumentRenderXlsx: `/patient-centric-app-v2/neo/documents/render-xlsx`,
  apiNeoDocumentRenderStyledTable: `/patient-centric-app-v2/neo/documents/render-styled-table`,
  apiNeoStandaloneArtifactDownloadUrl: `/patient-centric-app-v2/neo/documents/artifacts/{file_id}/download-url`,

  // Neo agent - files (Anthropic Files API uploads).
  // POST is multipart/form-data so the frontend uses raw fetch
  // (APIProvider.POST is JSON-only).
  apiNeoFiles: `/patient-centric-app-v2/neo/files`,
  apiNeoFile: `/patient-centric-app-v2/neo/files/{neo_file_id}`,

  // Neo agent - speech-to-text. POST is multipart/form-data (audio blob), so
  // the frontend uses raw fetch (APIProvider.POST is JSON-only).
  apiNeoTranscribe: `/patient-centric-app-v2/neo/transcribe`,

  // Neo agent - workflow builder (node/edge graphs)
  apiNeoWorkflows: `/patient-centric-app-v2/neo/workflows`,
  apiNeoWorkflow: `/patient-centric-app-v2/neo/workflows/{workflow_id}`,
  apiNeoWorkflowGraph: `/patient-centric-app-v2/neo/workflows/{workflow_id}/graph`,
  apiNeoWorkflowRestoreLastRun: `/patient-centric-app-v2/neo/workflows/{workflow_id}/restore-last-run`,
  apiNeoWorkflowKeepCurrentVersion: `/patient-centric-app-v2/neo/workflows/{workflow_id}/keep-current-version`,
  apiNeoWorkflowNodeOutputSchema: `/patient-centric-app-v2/neo/workflows/{workflow_id}/nodes/{node_id}/generate-output-schema`,
  apiNeoWorkflowNodeClinicScope: `/patient-centric-app-v2/neo/workflows/{workflow_id}/nodes/{node_id}/check-clinic-scope`,
  apiNeoWorkflowBuildConversation: `/patient-centric-app-v2/neo/workflows/{workflow_id}/build-conversation`,
  apiNeoWorkflowRun: `/patient-centric-app-v2/neo/workflows/{workflow_id}/run`,
  apiNeoWorkflowRuns: `/patient-centric-app-v2/neo/workflows/{workflow_id}/runs`,
  apiNeoWorkflowRunProgress: `/patient-centric-app-v2/neo/workflows/runs/{run_id}/progress`,
  apiNeoWorkflowRunConversations: `/patient-centric-app-v2/neo/workflows/runs/{run_id}/conversations`,
  apiNeoWorkflowEligibleClinics: `/patient-centric-app-v2/neo/workflows/{workflow_id}/eligible-clinics`,
  apiNeoWorkflowEmailAddresses: `/patient-centric-app-v2/neo/workflows/{workflow_id}/email-addresses`,
  apiNeoWorkflowEmailBindings: `/patient-centric-app-v2/neo/workflows/{workflow_id}/email-bindings`,
  apiNeoWorkflowEmailBinding: `/patient-centric-app-v2/neo/workflows/{workflow_id}/email-bindings/{binding_id}`,
  // Onboarding: clinic-scoped workflows a user can opt into, and the per-user
  // opt-in (enable) call.
  //
  // ``available`` is the bare catalog (no per-user state). ``enabled`` returns
  // the SAME catalog annotated with the caller's current subscription
  // (``subscribed``) plus runtime state, so the onboarding step can pre-check
  // the toggles the user is already opted into. Onboarding reads ``enabled``;
  // ``available`` is kept for callers that only need the catalog.
  apiNeoWorkflowsAvailable: `/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/available`,
  apiNeoWorkflowsEnabled: `/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/enabled`,
  apiNeoWorkflowAssignUser: `/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/{workflow_id}/users/{user_id}`,
  // Manually trigger ONE occurrence of a clinic workflow (a1 / b<n> / c<n>).
  // Detached for every family: returns 202 with a poll_url.
  apiNeoClinicWorkflowRun: `/patient-centric-app-v2/neo/workflows/clinic/{clinic_id}/{workflow_id}/run`,
  apiMondayBriefingRun: `/patient-centric-app-v2/cronjob/monday-facility-briefing/runs/{run_id}`,
  apiMondayBriefingDeliveries: `/patient-centric-app-v2/cronjob/monday-facility-briefing/deliveries`,
  apiMondayBriefingDeliveryEmail: `/patient-centric-app-v2/cronjob/monday-facility-briefing/deliveries/{row_id}/email`,

  // Product Utilization dashboard (superadmin-only — backend gates on the
  // Cognito `sa` group). Powers src/pages/ProductUtilizationDashboard.
  apiProductUtilizationNeoConversations: `/patient-centric-app-v2/product-utilization/neo/conversations`,
  apiProductUtilizationNeoConversation: `/patient-centric-app-v2/product-utilization/neo/conversations/{conversation_id}`,
  // LLM natural-language filter: fans out Haiku sub-agents over a set of
  // conversation IDs and returns a verdict per conversation.
  apiProductUtilizationNeoConversationsFilter: `/patient-centric-app-v2/product-utilization/neo/conversations/filter`,

  // NP Productivity Timeline (superadmin-only — backend gates on the Cognito
  // `sa` group). Returns one 24h row of work events per Nurse Practitioner for
  // a chosen calendar day. Powers src/pages/NpProductivityTimeline.
  apiNpProductivityTimeline: `/patient-centric-app-v2/np-productivity/timeline`,
  // Window aggregation: team KPIs, note-lifecycle medians, per-NP typical-day
  // distributions, per-NP×clinic volume, and daily completed counts over a
  // 30/60/90-day window. Powers the NP Documentation Workflow dashboard.
  apiNpProductivityWindowSummary: `/patient-centric-app-v2/np-productivity/window-summary`,
} as const;

export type IAPIRoutes = typeof APIRoutes;
